import { z } from 'zod';
import { BRAND_SIZE_BUCKETS } from './brand';

const Range = (item: z.ZodTypeAny) =>
  z
    .object({
      min: item.optional(),
      max: item.optional(),
    })
    .strict();

/**
 * The structured filter object the AI produces via OpenAI tool calling.
 * Mirror this in `apps/api/src/lib/ai/prompts/keepa-nl-search.ts` tool
 * definition.
 *
 * .strict() prevents the model from sneaking in unsupported fields.
 *
 * `category` is a friendly name (e.g. "Pet Supplies", "Dog Treats", "Wireless
 * Earbuds"). It's NOT validated against an enum — the AI can suggest any
 * Amazon browse category and the server resolves it to a Keepa numeric ID
 * via Keepa's category-search endpoint at search time, with results cached
 * in the keepa_category_cache table.
 */
export const KeepaFilters = z
  .object({
    bsrCurrent: Range(z.number().int().nonnegative()).optional(),
    bsr30DayAvg: Range(z.number().int().nonnegative()).optional(),
    bsr90DayAvg: Range(z.number().int().nonnegative()).optional(),
    amazonPriceCurrent: Range(z.number().int().nonnegative()).optional(),
    newPriceCurrent: Range(z.number().int().nonnegative()).optional(),
    /**
     * Buy-box price band in cents. Distinct from `newPriceCurrent` because
     * buy-box is where you actually compete on wholesale — that's the price
     * a buyer pays after Amazon picks a seller. Maps to Keepa's
     * `current_BUY_BOX_SHIPPING` (price + shipping).
     */
    buyBoxPriceCurrent: Range(z.number().int().nonnegative()).optional(),
    reviewCount: Range(z.number().int().nonnegative()).optional(),
    rating: Range(z.number().min(1).max(5)).optional(),
    monthlySoldEstimate: Range(z.number().int().nonnegative()).optional(),
    /**
     * Keepa's "last known" actual sales count, distinct from
     * `monthlySoldEstimate` which is a forward-looking guess. Use this when
     * you want to gate on a hard "did at least N units sell last month"
     * floor — wholesale velocity check.
     */
    monthlySoldLastKnown: Range(z.number().int().nonnegative()).optional(),
    /**
     * Total offers on the listing (new + used + collectible). Wholesale
     * sweet spot is usually 2–15 — oligopolistic enough that nobody can
     * race the price to zero, but not single-seller (which usually means
     * brand-direct only or counterfeit risk).
     */
    totalOfferCount: Range(z.number().int().nonnegative()).optional(),
    /**
     * Count of NEW offers only. Tighter signal than totalOfferCount when
     * the listing has stale used/refurb noise.
     */
    currentCountNew: Range(z.number().int().nonnegative()).optional(),
    /**
     * Operational: hazardous-material listings can't ship FBA standard
     * lanes. Default behavior is to pass through; set `false` to exclude.
     */
    isHazMat: z.boolean().optional(),
    /**
     * Seller IDs to exclude from the buy box. Wholesale strategy almost
     * always wants to exclude Amazon's own seller ID (`ATVPDKIKX0DER`) so
     * we don't compete against the brand owner.
     */
    excludeBuyBoxSellers: z.array(z.string()).optional(),
    /**
     * When `false`, only count listings whose buy box passed Keepa's
     * "qualified" check — filters out the noise of dead / suppressed
     * buy boxes that would otherwise look "available".
     */
    buyBoxIsUnqualified: z.boolean().optional(),
    brand: z.array(z.string()).optional(),
    excludeBrand: z.array(z.string()).optional(),
    /**
     * Brand-size post-filter. Non-Keepa field — the adapter ignores it and
     * the route layer applies it to results after stamping each product
     * with a bucket from keepa_brand_cache. When omitted, the route uses
     * the tenant's defaultBrandSizeCap. NL prompt v2.6.0 emits this for
     * phrases like "small brands only" / "include big brands".
     */
    brandSize: z
      .object({
        allowed: z.array(z.enum(BRAND_SIZE_BUCKETS)).nonempty(),
      })
      .strict()
      .optional(),
    title: z
      .object({
        contains: z.string().optional(),
        excludes: z.array(z.string()).optional(),
      })
      .strict()
      .optional(),
    category: z.string().min(1).max(120).optional(),
    amazonInStock: z.boolean().optional(),
    listedAfter: z.string().datetime().optional(),
    sortBy: z.enum(['bsr', 'price', 'reviewCount', 'monthlySold', 'rating']).optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
    limit: z.number().int().min(1).max(100).default(50),
    rationale: z.string().min(1),
  })
  .strict();
export type KeepaFilters = z.infer<typeof KeepaFilters>;
